import api from './api';
import { LoginRequest, LoginResponse, User } from '../types';
import { demoAuthService } from './demoAuthService';
import { config } from '../config';

// Helper function to check if it's a network error
const isNetworkError = (error: any): boolean => {
  return !error.response && (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error') || error.message?.includes('ERR_NETWORK'));
};

// Helper function to detect if a token is a demo token
const isDemoToken = (token: string | null): boolean => {
  return token ? token.startsWith('demo_token_') : false;
};

// Global demo mode override (for toggle functionality)
let demoModeOverride: boolean | null = null;

// Demo mode persistence key
const DEMO_MODE_KEY = 'asknow_demo_mode';

export const authApi = {
  // Set demo mode override
  setDemoMode: (enabled: boolean) => {
    demoModeOverride = enabled;
    // Persist demo mode preference
    localStorage.setItem(DEMO_MODE_KEY, enabled.toString());
  },

  // Get current demo mode state
  getDemoMode: () => {
    // First check if there's an override
    if (demoModeOverride !== null) {
      return demoModeOverride;
    }
    
    // Check if there's a persisted preference
    const persisted = localStorage.getItem(DEMO_MODE_KEY);
    if (persisted !== null) {
      return persisted === 'true';
    }
    
    // Check if current token is a demo token
    const currentToken = localStorage.getItem('token');
    if (currentToken && isDemoToken(currentToken)) {
      return true;
    }
    
    // Fall back to config default
    return config.shouldUseDemoMode();
  },

  // Detect if current user is from demo mode
  isCurrentUserDemo: (): boolean => {
    const token = localStorage.getItem('token');
    return isDemoToken(token);
  },

  // Initialize demo mode based on current state
  initializeDemoMode: () => {
    const token = localStorage.getItem('token');
    if (token && isDemoToken(token)) {
      // User is logged in with demo token, force demo mode
      demoModeOverride = true;
      localStorage.setItem(DEMO_MODE_KEY, 'true');
    } else if (token && !isDemoToken(token)) {
      // User is logged in with real token, force real API mode
      demoModeOverride = false;
      localStorage.setItem(DEMO_MODE_KEY, 'false');
    }
    // If no token, use default behavior
  },

  // Login user
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    if (authApi.getDemoMode()) {
      return demoAuthService.login(credentials);
    }
    
    try {
      const response = await api.post('/auth/login', credentials);
      return response.data;
    } catch (error: any) {
      // Check if it's a network error
      if (isNetworkError(error)) {
        console.error('Network error - API server may be down:', error.message);
        throw new Error('Unable to connect to server. Please check your internet connection or try again later.');
      }
      
      // Re-throw the original error
      throw error;
    }
  },

  // Register user
  register: async (userData: any): Promise<any> => {
    if (authApi.getDemoMode()) {
      throw new Error('Registration not available in demo mode');
    }
    
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error: any) {
      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection or try again later.');
      }
      throw error;
    }
  },

  // Get current user
  getCurrentUser: async (): Promise<User> => {
    if (authApi.getDemoMode()) {
      return demoAuthService.getCurrentUser();
    }
    
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error: any) {
      // Check if it's a network error
      if (isNetworkError(error)) {
        console.error('Network error - API server may be down:', error.message);
        throw new Error('Unable to connect to server. Please check your internet connection or try again later.');
      }
      
      // Re-throw the original error
      throw error;
    }
  },

  // Refresh token
  refreshToken: async (): Promise<any> => {
    if (authApi.getDemoMode()) {
      throw new Error('Token refresh not available in demo mode');
    }
    
    try {
      const response = await api.post('/auth/refresh');
      return response.data;
    } catch (error: any) {
      if (isNetworkError(error)) {
        throw new Error('Unable to connect to server. Please check your internet connection or try again later.');
      }
      throw error;
    }
  },

  // Logout user
  logout: async (): Promise<void> => {
    if (authApi.getDemoMode()) {
      return demoAuthService.logout();
    }
    
    try {
      await api.post('/auth/logout');
    } catch (error: any) {
      // Even if logout fails, clear local storage
      console.warn('Logout request failed, but clearing local storage');
      if (isNetworkError(error)) {
        console.warn('Network error during logout:', error.message);
      }
    }
  },

  // Get demo credentials (for display purposes)
  getDemoCredentials: () => {
    return config.demoCredentials;
  },
};
